import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FiAtSign, FiMessageSquare } from "react-icons/fi";
import { useSelector } from "react-redux";
import { ownerSelector } from "../store/store";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "../utils/axios";
import { fetchCampaigns, fetchUserOwnersWithWorkspaces } from "../utils";
import { setInboxUnreadChatsCount } from "../utils/inboxUnread";
import ConnectLinkedinCredentialsSidebar from "../components/Accounts/components/ConnectLinkedinCredentialsSidebar";
import ConnectLinkedinExtensionSidebar from "../components/Accounts/components/ConnectLinkedinExtensionSidebar";
import { ConnectLinkedinModal } from "../components/Modals";
import { Button, Select } from "../components/ui";

type ConversationItem = {
  conversationUrn: string;
  backendUrn: string;
  threadId: string;
  participantName: string;
  participantEntityUrn: string;
  participantImageUrl?: string;
  unreadCount: number;
  lastActivityAt: number | null;
  lastMessageFrom?: "sender" | "lead";
  campaignName?: string;
  lastMessageText: string;
};

type MessageItem = {
  messageUrn: string;
  senderEntityUrn: string;
  senderName: string;
  text: string;
  deliveredAt: number | null;
  isFromOwner: boolean;
};
type AccountStatus = "" | "ok" | "not_connected" | "disconnected" | "unavailable";

const INBOX_CONVERSATIONS_RETRY_ATTEMPTS = 2;
const INBOX_CONVERSATIONS_RETRY_DELAY_MS = 700;

const mergeConversationsByThreadId = (
  existing: ConversationItem[],
  incoming: ConversationItem[]
): ConversationItem[] => {
  const map = new Map<string, ConversationItem>();
  for (const conv of existing) {
    const key = String(conv?.threadId || "").trim();
    if (key) map.set(key, conv);
  }
  for (const conv of incoming) {
    const key = String(conv?.threadId || "").trim();
    if (!key) continue;
    map.set(key, conv);
  }
  return Array.from(map.values()).sort(
    (a, b) => (b.lastActivityAt || 0) - (a.lastActivityAt || 0)
  );
};

const oldestLastActivityAt = (list: ConversationItem[]): number | null => {
  let oldest: number | null = null;
  for (const conv of list) {
    const ts = Number(conv?.lastActivityAt || 0);
    if (!Number.isFinite(ts) || ts <= 0) continue;
    if (oldest == null || ts < oldest) oldest = ts;
  }
  return oldest;
};

const Inbox = () => {
  const { owner, currentUser, currentWorkspace } = useSelector(ownerSelector);
  const navigate = useNavigate();
  const location = useLocation();
  const { threadId: routeThreadId } = useParams();
  const [senderAccounts, setSenderAccounts] = useState<
    Array<{
      ownerId: string;
      name: string;
      email: string;
      picture: string;
      hasLinkedinToken: boolean;
      isDisconnected: boolean;
    }>
  >([]);
  const [selectedSenderOwnerId, setSelectedSenderOwnerId] = useState("");
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [refreshingThread, setRefreshingThread] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageDraft, setMessageDraft] = useState("");
  const [accountMessage, setAccountMessage] = useState("");
  const [accountStatus, setAccountStatus] = useState<AccountStatus>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [draftCampaign, setDraftCampaign] = useState("all");
  const [draftLastMessageFrom, setDraftLastMessageFrom] = useState("all");
  const [appliedCampaign, setAppliedCampaign] = useState("all");
  const [appliedLastMessageFrom, setAppliedLastMessageFrom] = useState("all");
  const [activeMessageTab, setActiveMessageTab] = useState<
    "all" | "linkedin" | "email" | "whatsapp" | "sms" | "unread"
  >("all");
  const [accountCampaigns, setAccountCampaigns] = useState<Array<{ value: string; label: string }>>([]);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showReconnectSidebar, setShowReconnectSidebar] = useState(false);
  const [showConnectExtensionSidebar, setShowConnectExtensionSidebar] = useState(false);
  const [isExtensionDetectionLoading, setIsExtensionDetectionLoading] = useState(false);
  const [connectSidebarMode, setConnectSidebarMode] = useState<"manual" | "extension">(
    "extension"
  );
  const [activeExtensionId, setActiveExtensionId] = useState("");
  const [reconnectOwnerId, setReconnectOwnerId] = useState("");
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const conversationsScrollRef = useRef<HTMLDivElement | null>(null);
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(false);
  const cursorRef = useRef<number | null>(null);
  const nextCursorRef = useRef("");
  const loadMoreConversationsRef = useRef<() => Promise<void> | void>(() => {});
  const markSeenInFlightRef = useRef<Set<string>>(new Set());
  const conversationsRef = useRef<ConversationItem[]>([]);
  const activeOwnerIdRef = useRef<string>("");
  const conversationsFetchPromiseByOwnerRef = useRef<Map<string, Promise<ConversationItem[]>>>(
    new Map()
  );
  const senderOptions = useMemo(() => {
    const fromApi = senderAccounts.map((item) => ({
      ownerId: String(item.ownerId || ""),
      name: String(item.name || "").trim(),
      email: String(item.email || "").trim(),
      picture: String(item.picture || "").trim(),
      hasLinkedinToken: Boolean(item.hasLinkedinToken),
      isDisconnected: Boolean(item.isDisconnected),
    }))
    .filter((entry) => !entry.isDisconnected);
    const map = new Map<string, any>();
    fromApi.forEach((entry) => {
      if (!entry.ownerId) return;
      if (!map.has(entry.ownerId)) map.set(entry.ownerId, entry);
    });
    return Array.from(map.values());
  }, [senderAccounts]);
  /** Sender id used for inbox API calls — must stay inside `senderOptions`; never fall back to workspace `owner._id` when that id is not a listed sender (avoids “tokens missing” for the wrong Mongo owner while the UI shows the first dropdown option). */
  const activeOwnerId = useMemo(() => {
    if (!senderOptions.length) {
      return String(selectedSenderOwnerId || "").trim();
    }
    const validIds = new Set(senderOptions.map((s) => String(s.ownerId)));
    const chosen = String(selectedSenderOwnerId || "").trim();
    if (chosen && validIds.has(chosen)) return chosen;
    const workspace = String(owner?._id || "").trim();
    if (workspace && validIds.has(workspace)) return workspace;
    const withToken = senderOptions.find((s) => s.hasLinkedinToken);
    return String((withToken || senderOptions[0])?.ownerId || "");
  }, [senderOptions, selectedSenderOwnerId, owner?._id]);

  const selectedSender = useMemo(() => {
    const id = String(activeOwnerId || "");
    if (!id) return null;
    return senderOptions.find((entry) => String(entry.ownerId) === id) || null;
  }, [senderOptions, activeOwnerId]);

  const senderLabel =
    selectedSender?.name ||
    selectedSender?.email ||
    owner?.name?.trim() ||
    owner?.email?.trim() ||
    "LinkedIn account";
  const ownerImageUrl =
    selectedSender?.picture || owner?.picture || owner?.profilePic || "";
  const isLinkedinConnected = Boolean(
    selectedSender?.hasLinkedinToken ||
      (String(owner?._id) === String(activeOwnerId) && owner?.tokens?.linkedin?.li_at)
  );

  const selectedThreadId = useMemo(
    () => decodeURIComponent(routeThreadId || ""),
    [routeThreadId]
  );
  const selectedConversation = useMemo(
    () => conversations.find((conv) => conv.threadId === selectedThreadId) || null,
    [conversations, selectedThreadId]
  );

  const filteredConversations = useMemo(() => {
    return conversations.filter((conv) => {
      const campaignOk =
        appliedCampaign === "all" ||
        String(conv?.campaignName || "").trim() === appliedCampaign;
      const fromVal = String(conv?.lastMessageFrom || "lead").toLowerCase();
      const fromOk =
        appliedLastMessageFrom === "all" ||
        (appliedLastMessageFrom === "sender" && fromVal === "sender") ||
        (appliedLastMessageFrom === "lead" && fromVal === "lead");
      return campaignOk && fromOk;
    });
  }, [appliedCampaign, appliedLastMessageFrom, conversations]);

  const unreadChatsCount = useMemo(
    () => filteredConversations.filter((conv) => Number(conv.unreadCount || 0) > 0).length,
    [filteredConversations]
  );

  useEffect(() => {
    setInboxUnreadChatsCount(unreadChatsCount);
  }, [unreadChatsCount]);

  const tabFilteredConversations = useMemo(() => {
    if (activeMessageTab === "unread") {
      return filteredConversations.filter((conv) => Number(conv.unreadCount || 0) > 0);
    }
    if (activeMessageTab === "email" || activeMessageTab === "whatsapp" || activeMessageTab === "sms") {
      return [];
    }
    return filteredConversations;
  }, [activeMessageTab, filteredConversations]);

  const isDisconnectedState = useMemo(() => accountStatus === "disconnected", [accountStatus]);
  const shouldShowDisconnectedEmptyState = isDisconnectedState && conversations.length === 0;
  const searchedConversations = useMemo(() => {
    const query = String(searchQuery || "").trim().toLowerCase();
    if (!query) return tabFilteredConversations;
    return tabFilteredConversations.filter((conv) => {
      const participant = String(conv?.participantName || "").toLowerCase();
      const lastMessage = String(conv?.lastMessageText || "").toLowerCase();
      return participant.includes(query) || lastMessage.includes(query);
    });
  }, [searchQuery, tabFilteredConversations]);
  const conversationsForUi = useMemo(() => searchedConversations, [searchedConversations]);

  const applyInboxPagination = useCallback(
    (hasMore: boolean, cursor: number | null, nextCursor = "") => {
      hasMoreRef.current = hasMore;
      cursorRef.current = cursor;
      nextCursorRef.current = String(nextCursor || "").trim();
    },
    []
  );

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    activeOwnerIdRef.current = String(activeOwnerId || "").trim();
  }, [activeOwnerId]);

  useEffect(() => {
    if (!activeOwnerId) return;
    if (String(selectedSenderOwnerId || "") === activeOwnerId) return;
    setSelectedSenderOwnerId(activeOwnerId);
  }, [activeOwnerId, selectedSenderOwnerId]);

  const loadMoreConversations = useCallback(async () => {
    if (!activeOwnerId || loadingConversations || loadingMoreRef.current) return;
    if (!hasMoreRef.current) return;
    const cursor = cursorRef.current || oldestLastActivityAt(conversationsRef.current);
    const pageCursor = String(nextCursorRef.current || "").trim();
    if (!cursor && !pageCursor) return;
    const ownerKey = String(activeOwnerId || "").trim();
    loadingMoreRef.current = true;
    setLoadingMoreConversations(true);
    try {
      const existingIds = new Set(
        conversationsRef.current
          .map((conv) => String(conv?.threadId || "").trim())
          .filter(Boolean)
      );
      const response = await axios.post("/linkedin/inboxConversations", {
        ownerId: activeOwnerId,
        ...(cursor ? { lastUpdatedBefore: cursor } : {}),
        ...(pageCursor ? { nextCursor: pageCursor } : {}),
      });
      if (activeOwnerIdRef.current !== ownerKey) return;
      const responseCode = String(response?.data?.code || "")
        .trim()
        .toUpperCase();
      if (responseCode !== "OK") {
        applyInboxPagination(false, null, "");
        return;
      }
      const incoming = Array.isArray(response?.data?.conversations)
        ? response.data.conversations
        : [];
      const incomingNextCursor = String(response?.data?.nextCursor || "").trim();
      const newCount = incoming.filter((conv: ConversationItem) => {
        const id = String(conv?.threadId || "").trim();
        return Boolean(id) && !existingIds.has(id);
      }).length;
      const merged = mergeConversationsByThreadId(conversationsRef.current, incoming);
      conversationsRef.current = merged;
      setConversations(merged);
      if (newCount === 0) {
        if (incomingNextCursor && incomingNextCursor !== pageCursor) {
          const apiCursor = Number(response?.data?.nextLastUpdatedBefore);
          const fromApi = Number.isFinite(apiCursor) && apiCursor > 0 ? apiCursor : null;
          applyInboxPagination(true, fromApi || cursor, incomingNextCursor);
          loadingMoreRef.current = false;
          setLoadingMoreConversations(false);
          void loadMoreConversationsRef.current();
          return;
        }
        applyInboxPagination(false, null, "");
        return;
      }
      const apiCursor = Number(response?.data?.nextLastUpdatedBefore);
      const fromApi = Number.isFinite(apiCursor) && apiCursor > 0 ? apiCursor : null;
      const derived = oldestLastActivityAt(merged);
      let nextTimestamp: number | null = null;
      if (fromApi && cursor && fromApi < cursor) nextTimestamp = fromApi;
      else if (derived && cursor && derived < cursor) nextTimestamp = derived;
      else if (derived) nextTimestamp = derived;
      else if (cursor) nextTimestamp = cursor - 1;
      const canContinue =
        Boolean(incomingNextCursor) ||
        Boolean(response?.data?.hasMore) ||
        Boolean(nextTimestamp && nextTimestamp > 0);
      applyInboxPagination(
        canContinue,
        nextTimestamp && nextTimestamp > 0 ? nextTimestamp : null,
        incomingNextCursor
      );
    } catch {
      applyInboxPagination(true, cursor, pageCursor);
    } finally {
      if (!loadingMoreRef.current) return;
      loadingMoreRef.current = false;
      setLoadingMoreConversations(false);
      window.requestAnimationFrame(() => {
        const el = conversationsScrollRef.current;
        if (!el || !hasMoreRef.current) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
          void loadMoreConversationsRef.current();
        }
      });
    }
  }, [activeOwnerId, loadingConversations, applyInboxPagination]);
  loadMoreConversationsRef.current = loadMoreConversations;

  useEffect(() => {
    if (loadingConversations || loadingMoreConversations) return;
    const el = conversationsScrollRef.current;
    if (!el || !hasMoreRef.current) return;
    if (el.scrollHeight > el.clientHeight + 4) return;
    void loadMoreConversationsRef.current();
  }, [conversations, loadingConversations, loadingMoreConversations]);

  const onConversationsScroll = () => {
    const el = conversationsScrollRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 4) return;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 80) return;
    void loadMoreConversationsRef.current();
  };

  useEffect(() => {
    const loadSenderAccounts = async () => {
      if (!currentUser?.userId && !owner?._id) {
        return;
      }
      try {
        const result = await fetchUserOwnersWithWorkspaces(
          1,
          100,
          "",
          currentUser?.userId,
          currentWorkspace?._id,
          owner?._id
        );
        const accounts = Array.isArray(result?.accounts) ? result.accounts : [];
        setSenderAccounts(
          accounts.map((item: any) => ({
            ownerId: String(item?._id || ""),
            name: String(item?.name || "").trim(),
            email: String(item?.email || "").trim(),
            picture: String(item?.picture || "").trim(),
            hasLinkedinToken: Boolean(item?.hasLinkedinToken),
            isDisconnected: Boolean(item?.isDisconnected),
          }))
        );
      } catch (_) {
      }
    };
    loadSenderAccounts();
  }, [currentUser?.userId, currentWorkspace?._id, owner?._id]);

  useEffect(() => {
    const loadCampaignOptions = async () => {
      if (!activeOwnerId) {
        setAccountCampaigns([]);
        return;
      }
      try {
        const campaigns = await fetchCampaigns(
          activeOwnerId,
          currentUser?.userId,
          currentWorkspace?._id
        );
        const options = Array.from(
          new Map(
            (Array.isArray(campaigns) ? campaigns : [])
              .map((campaign: any) => {
                const name = String(campaign?.campaignName || campaign?.name || "").trim();
                return name ? [name, { value: name, label: name }] : null;
              })
              .filter(Boolean) as Array<[string, { value: string; label: string }]>
          ).values()
        );
        setAccountCampaigns(options);
      } catch (error) {
        setAccountCampaigns([]);
      }
    };
    loadCampaignOptions();
  }, [activeOwnerId, currentUser?.userId, currentWorkspace?._id]);

  const loadConversations = useCallback(async (options?: { force?: boolean }) => {
    if (!activeOwnerId) {
      setConversations([]);
      setMessages([]);
      setAccountMessage("Connect your account");
      setAccountStatus("not_connected");
      return [];
    }
    const ownerKey = String(activeOwnerId || "").trim();
    const shouldForce = Boolean(options?.force);
    if (!shouldForce) {
      const inFlight = conversationsFetchPromiseByOwnerRef.current.get(ownerKey);
      if (inFlight) return inFlight;
    }
    const run = (async () => {
    setAccountMessage("");
    setAccountStatus("");
    applyInboxPagination(false, null, "");
    loadingMoreRef.current = false;
    setLoadingConversations(true);
    try {
      for (let attempt = 1; attempt <= INBOX_CONVERSATIONS_RETRY_ATTEMPTS; attempt += 1) {
        try {
          if (activeOwnerIdRef.current !== ownerKey) {
            return conversationsRef.current;
          }
          const response = await axios.post("/linkedin/inboxConversations", {
            ownerId: activeOwnerId,
          });
          if (activeOwnerIdRef.current !== ownerKey) {
            return conversationsRef.current;
          }
          const responseCode = String(response?.data?.code || "")
            .trim()
            .toUpperCase();
          const isNotConnected = responseCode === "ACCOUNT_NOT_CONNECTED";
          const isDisconnected = responseCode === "ACCOUNT_DISCONNECTED";
          const isUnavailable = responseCode === "INBOX_UNAVAILABLE";
          if (isNotConnected || isDisconnected || isUnavailable) {
            if (attempt < INBOX_CONVERSATIONS_RETRY_ATTEMPTS) {
              if (activeOwnerIdRef.current !== ownerKey) {
                return conversationsRef.current;
              }
              await new Promise((resolve) =>
                window.setTimeout(resolve, INBOX_CONVERSATIONS_RETRY_DELAY_MS)
              );
              continue;
            }
            setConversations([]);
            setMessages([]);
            if (isUnavailable) {
              setAccountMessage("Please try again later");
              setAccountStatus("unavailable");
            } else {
              setAccountMessage(
                isNotConnected ? "Connect your account" : "Your account is disconnected"
              );
              setAccountStatus(isNotConnected ? "not_connected" : "disconnected");
            }
            return [];
          }

          const nextConversations = Array.isArray(response?.data?.conversations)
            ? response.data.conversations
            : [];
          setConversations(nextConversations);
          conversationsRef.current = nextConversations;
          const apiCursor = Number(response?.data?.nextLastUpdatedBefore);
          const fromApi = Number.isFinite(apiCursor) && apiCursor > 0 ? apiCursor : null;
          const derived = oldestLastActivityAt(nextConversations);
          const cursor = fromApi || derived;
          const incomingNextCursor = String(response?.data?.nextCursor || "").trim();
          applyInboxPagination(
            Boolean(response?.data?.hasMore) || Boolean(incomingNextCursor) || Boolean(cursor),
            cursor,
            incomingNextCursor
          );
          setAccountMessage("");
          setAccountStatus("ok");
          return nextConversations;
        } catch (error) {
          if (activeOwnerIdRef.current !== ownerKey) {
            return conversationsRef.current;
          }
          if (attempt < INBOX_CONVERSATIONS_RETRY_ATTEMPTS) {
            if (activeOwnerIdRef.current !== ownerKey) {
              return conversationsRef.current;
            }
            await new Promise((resolve) =>
              window.setTimeout(resolve, INBOX_CONVERSATIONS_RETRY_DELAY_MS)
            );
            continue;
          }
          throw error;
        }
      }
      return [];
    } catch (error) {
      setConversations([]);
      setMessages([]);
      setAccountMessage("Please try again later");
      setAccountStatus("unavailable");
      return [];
    } finally {
      setLoadingConversations(false);
      conversationsFetchPromiseByOwnerRef.current.delete(ownerKey);
    }
    })();
    if (!shouldForce) {
      conversationsFetchPromiseByOwnerRef.current.set(ownerKey, run);
    }
    return run;
  }, [activeOwnerId, applyInboxPagination]);

  useEffect(() => {
    loadConversations();
  }, [activeOwnerId, loadConversations]);

  // Sidebar "Inbox" uses navigation state so we return to the default all-chats view.
  useEffect(() => {
    const inboxReset = Boolean(
      (location.state as { inboxReset?: boolean } | null)?.inboxReset
    );
    if (!inboxReset) return;
    setSearchQuery("");
    setAppliedCampaign("all");
    setAppliedLastMessageFrom("all");
    setDraftCampaign("all");
    setDraftLastMessageFrom("all");
    setActiveMessageTab("all");
    applyInboxPagination(false, null, "");
    markSeenInFlightRef.current.clear();
    void loadConversations();
    navigate("/inbox", { replace: true, state: {} });
  }, [location.state, navigate, loadConversations]);

  useEffect(() => {
    if (!selectedThreadId && conversations.length > 0 && conversations[0].threadId) {
      navigate(`/inbox/thread/${encodeURIComponent(conversations[0].threadId)}`, {
        replace: true,
      });
      return;
    }
    if (selectedThreadId) {
      const selectedStillExists = conversations.some(
        (conversation: ConversationItem) =>
          conversation.threadId === selectedThreadId
      );
      if (!selectedStillExists) {
        setMessages([]);
        navigate("/inbox", { replace: true });
      }
    }
  }, [conversations, navigate, selectedThreadId]);

  const loadMessages = useCallback(async () => {
    if (!activeOwnerId || !selectedThreadId) {
      setMessages([]);
      return;
    }
    setLoadingMessages(true);
    try {
      const response = await axios.post("/linkedin/inboxConversationMessages", {
        ownerId: activeOwnerId,
        threadId: selectedThreadId,
        conversationUrn: selectedConversation?.conversationUrn,
      });
      const responseCode = String(response?.data?.code || "");
      if (responseCode === "ACCOUNT_NOT_CONNECTED") {
        setMessages([]);
        setAccountMessage("Connect your account");
        setAccountStatus("not_connected");
        return;
      }
      if (responseCode === "ACCOUNT_DISCONNECTED") {
        setMessages([]);
        setAccountMessage("Your account is disconnected");
        setAccountStatus("disconnected");
        return;
      }
      if (responseCode === "INBOX_UNAVAILABLE") {
        setMessages([]);
        setAccountMessage("Please try again later");
        setAccountStatus("unavailable");
        return;
      }
      setAccountStatus("ok");
      setMessages(Array.isArray(response?.data?.messages) ? response.data.messages : []);
    } catch (error) {
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, [activeOwnerId, selectedConversation?.conversationUrn, selectedThreadId]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useLayoutEffect(() => {
    if (loadingMessages || refreshingThread) return;
    const el = messagesScrollRef.current;
    if (!el) return;
    const scrollToBottom = () => {
      el.scrollTop = el.scrollHeight;
    };
    scrollToBottom();
    const id = window.requestAnimationFrame(scrollToBottom);
    return () => window.cancelAnimationFrame(id);
  }, [messages, loadingMessages, refreshingThread, selectedThreadId]);

  const formatTime = (ts: number | null) => {
    if (!ts) return "";
    try {
      return new Date(ts).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
    } catch (err) {
      return "";
    }
  };

  const formatCardDate = (ts: number | null) => {
    if (!ts) return "";
    try {
      return new Date(ts).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }).toUpperCase();
    } catch (err) {
      return "";
    }
  };

  const formatRelativeTime = (ts: number | null) => {
    if (!ts) return "";
    const diffMs = Date.now() - ts;
    const mins = Math.max(1, Math.round(diffMs / 60000));
    if (mins < 60) return `${mins}m`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h`;
    return formatCardDate(ts);
  };

  const getInitials = (name: string) => {
    const parts = String(name || "")
      .split(" ")
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  };

  const isOwnerMessage = (message: MessageItem) => {
    if (message.isFromOwner) return true;
    const sender = String(message.senderName || "").trim().toLowerCase();
    const ownerName = String(senderLabel || "").trim().toLowerCase();
    return Boolean(sender && ownerName && sender === ownerName);
  };

  const truncatePreview = (text: string, maxLength = 44) => {
    const normalized = String(text || "").replace(/\s+/g, " ").trim();
    if (!normalized) return "";
    if (normalized.length <= maxLength) return normalized;
    return `${normalized.slice(0, maxLength - 3).trim()}...`;
  };

  const markConversationSeen = useCallback(
    async (conversation: ConversationItem | null) => {
      if (!activeOwnerId || !conversation) return;
      if (Number(conversation.unreadCount || 0) <= 0) return;
      const threadKey = String(conversation.threadId || "");
      if (!threadKey) return;
      if (markSeenInFlightRef.current.has(threadKey)) return;
      markSeenInFlightRef.current.add(threadKey);
      try {
        const response = await axios.post("/linkedin/inboxMarkSeen", {
          ownerId: activeOwnerId,
          threadId: conversation.threadId,
          conversationUrn: conversation.conversationUrn,
          until: Date.now(),
        });
        if (String(response?.data?.code || "") === "OK") {
          setConversations((prev) =>
            prev.map((item) =>
              item.threadId === conversation.threadId
                ? { ...item, unreadCount: 0 }
                : item
            )
          );
        }
      } catch (_) {
      } finally {
        markSeenInFlightRef.current.delete(threadKey);
      }
    },
    [activeOwnerId]
  );

  const onSelectConversation = (threadId: string) => {
    // Same flow as auto-opened first chat: navigate first, then mark-seen runs from
    // useEffect when selectedConversation updates (avoids duplicate/racy calls).
    navigate(`/inbox/thread/${encodeURIComponent(threadId)}`);
  };

  useEffect(() => {
    markConversationSeen(selectedConversation);
  }, [markConversationSeen, selectedConversation]);

  const onRefreshThread = async () => {
    if (!selectedThreadId) return;
    setRefreshingThread(true);
    try {
      await loadMessages();
    } finally {
      setRefreshingThread(false);
    }
  };

  const onSendMessage = async () => {
    const text = messageDraft.trim();
    if (!text || !activeOwnerId || !selectedConversation) return;
    setSendingMessage(true);
    try {
      const response = await axios.post("/linkedin/inboxSendMessage", {
        ownerId: activeOwnerId,
        threadId: selectedConversation.threadId,
        conversationUrn: selectedConversation.conversationUrn,
        recipientEntityUrn: selectedConversation.participantEntityUrn,
        message: text,
      });
      const responseCode = String(response?.data?.code || "");
      if (responseCode === "ACCOUNT_NOT_CONNECTED") {
        setAccountMessage("Connect your account");
        setAccountStatus("not_connected");
        return;
      }
      if (responseCode === "ACCOUNT_DISCONNECTED") {
        setAccountMessage("Your account is disconnected");
        setAccountStatus("disconnected");
        return;
      }
      if (responseCode === "INBOX_UNAVAILABLE") {
        setAccountMessage("Please try again later");
        setAccountStatus("unavailable");
        return;
      }
      setAccountStatus("ok");
      setMessageDraft("");
      await loadMessages();
    } finally {
      setSendingMessage(false);
    }
  };

  const getExtensionId = () => {
    const marker = document.getElementById("SCR_Extension_Installed");
    const markerExtensionId = marker?.getAttribute("data-extension-id") || "";
    const maybeWindow = window as Window & {
      __INNLEAD_EXTENSION_ID__?: string;
    };
    const webStoreUrl = process.env.REACT_APP_CHROME_WEBSTORE_URL || "";
    const webStoreMatch = webStoreUrl.match(/\/detail\/[^/]+\/([a-z]{32})(?:[/?#]|$)/i);
    const extensionIdFromStoreUrl = webStoreMatch?.[1] || "";
    return (
      markerExtensionId ||
      maybeWindow.__INNLEAD_EXTENSION_ID__ ||
      process.env.REACT_APP_CHROME_EXTENSION_ID ||
      extensionIdFromStoreUrl ||
      ""
    );
  };

  const pingInstalledExtension = async () => {
    const extensionId = getExtensionId();
    const browserChrome = (window as Window & { chrome?: any }).chrome;
    const chromeRuntime = browserChrome?.runtime;
    if (!extensionId || !chromeRuntime?.sendMessage) {
      return false;
    }
    return new Promise<boolean>((resolve) => {
      let resolved = false;
      const timeout = window.setTimeout(() => {
        if (!resolved) {
          resolved = true;
          resolve(false);
        }
      }, 1000);
      try {
        chromeRuntime.sendMessage(extensionId, { type: "PING_INNLEAD_EXTENSION" }, () => {
          if (resolved) return;
          window.clearTimeout(timeout);
          resolved = true;
          const chromeLastError = browserChrome?.runtime?.lastError;
          resolve(!chromeLastError);
        });
      } catch {
        if (!resolved) {
          window.clearTimeout(timeout);
          resolved = true;
          resolve(false);
        }
      }
    });
  };

  const detectExtensionInstalled = async () => {
    const firstCheck = await pingInstalledExtension();
    if (firstCheck) return true;
    await new Promise((resolve) => window.setTimeout(resolve, 300));
    return pingInstalledExtension();
  };

  const onReconnectSender = () => {
    const senderOwnerId = String(activeOwnerId || "").trim();
    if (!senderOwnerId) return;
    setReconnectOwnerId(senderOwnerId);
    setConnectSidebarMode("extension");
    setShowConnectModal(true);
  };

  useEffect(() => {
    if (!showConnectExtensionSidebar || isExtensionDetectionLoading) return;
    let disposed = false;
    const interval = window.setInterval(async () => {
      const installed = await detectExtensionInstalled();
      if (!installed || disposed) return;
      setActiveExtensionId(getExtensionId());
      setConnectSidebarMode("extension");
      setShowReconnectSidebar(true);
      setShowConnectExtensionSidebar(false);
      setShowConnectModal(false);
    }, 1200);
    return () => {
      disposed = true;
      window.clearInterval(interval);
    };
  }, [showConnectExtensionSidebar, isExtensionDetectionLoading]);

  return (
    <section className="noScroll flex h-full min-h-0 flex-col overflow-hidden bg-paper px-4 py-6 sm:px-9 sm:py-9">
      <div className="mx-auto flex h-full min-h-0 w-full max-w-[1600px] flex-col">
      <header className="mb-[22px] shrink-0">
        <h1 className="text-[27px] font-semibold tracking-tight text-dark-blue">Conversations</h1>
        <p className="mt-1 text-sm text-muted">
          Respond to high-intent prospects from LinkedIn, email, WhatsApp, and SMS in one place.
        </p>
      </header>

      <div className="mb-4 flex shrink-0 flex-wrap items-center gap-2">
        <Select
          variant="compact"
          size="sm"
          ariaLabel="Select sender"
          value={activeOwnerId}
          disabled={senderOptions.length === 0}
          onChange={(nextOwnerId) => {
            if (!nextOwnerId || nextOwnerId === selectedSenderOwnerId) return;
            setLoadingConversations(true);
            setConversations([]);
            setMessages([]);
            setAccountMessage("");
            setActiveMessageTab("all");
            setSelectedSenderOwnerId(nextOwnerId);
            navigate("/inbox", { replace: true });
          }}
          options={
            senderOptions.length > 0
              ? senderOptions.map((entry) => ({
                  value: entry.ownerId,
                  label: entry.name || entry.email || "LinkedIn account",
                }))
              : [{ value: "", label: "All owners" }]
          }
          className="h-10 w-[170px] !border-line font-semibold text-dark-blue"
        />
      </div>

      <div className="grid min-h-0 flex-1 overflow-hidden rounded-[10px] border border-line bg-white lg:grid-cols-[270px_minmax(320px,1fr)_230px] lg:grid-rows-[minmax(0,1fr)]">
          <aside
            className={`flex h-full min-h-[50vh] w-full shrink-0 flex-col overflow-hidden border-line p-[13px] lg:min-h-0 ${
              selectedThreadId ? "hidden lg:flex" : "flex"
            } lg:border-r`}
          >
            <div className="mb-2 flex shrink-0 flex-wrap items-center gap-[5px]">
              {(
                [
                  { id: "all", label: "All" },
                  { id: "linkedin", label: "LinkedIn" },
                  {
                    id: "unread",
                    label: unreadChatsCount > 0 ? `Unread ${unreadChatsCount > 99 ? "99+" : unreadChatsCount}` : "Unread",
                  },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`rounded-[5px] px-1.5 py-1.5 text-[10px] ${
                    activeMessageTab === tab.id
                      ? "bg-sidebar-chip text-white"
                      : "bg-inbox-filter text-muted"
                  }`}
                  onClick={() => setActiveMessageTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="relative h-0 min-h-0 flex-1">
            <div
              ref={conversationsScrollRef}
              onScroll={onConversationsScroll}
              className="inboxScrollbar absolute inset-0 overflow-y-auto overscroll-contain"
            >
              {loadingConversations ? (
                <div className="mt-6 flex flex-col items-center justify-center gap-2">
                  <img
                    src="/logo/loading.svg"
                    alt="Loading conversations"
                    className="h-10 w-10 rounded-xl animate-pulse"
                  />
                  <p className="text-xs text-dark-grey">Loading conversations...</p>
                </div>
              ) : shouldShowDisconnectedEmptyState ? null : !isLinkedinConnected && conversations.length === 0 ? (
                <div className="mt-8 flex flex-col items-center justify-center text-center">
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-paper text-muted">
                    <FiMessageSquare size={16} />
                  </div>
                  <p className="text-xs font-medium text-dark-black">No messages yet</p>
                  <p className="mt-1 max-w-[200px] text-[11px] text-dark-grey">
                    Connect LinkedIn to load your inbox.
                  </p>
                  <button
                    className="mt-4 inline-flex items-center gap-2 rounded bg-bright-green px-3 py-1.5 text-xs font-semibold text-dark-black"
                    onClick={() => navigate("/teams", { state: { openConnectModal: true } })}
                  >
                    <FiAtSign size={12} />
                    Add your accounts
                  </button>
                </div>
              ) : conversationsForUi.length === 0 ? (
                <p className="mt-6 text-center text-xs text-dark-grey">No conversations found.</p>
              ) : (
                conversationsForUi.map((conversation) => {
                  const isDummy = String(conversation.threadId || "").startsWith("__dummy__");
                  const isActive = selectedThreadId === conversation.threadId;
                  return (
                    <button
                      key={conversation.conversationUrn || conversation.threadId}
                      className={`flex w-full items-start gap-2 border-t border-thread-line px-1 py-[11px] text-left text-xs ${
                        isActive
                          ? "-mx-[9px] bg-thread-active pl-[13px] pr-1"
                          : "bg-white"
                      }`}
                      onClick={() => {
                        if (isDummy) return;
                        onSelectConversation(conversation.threadId);
                      }}
                    >
                      <div className="mt-0.5 grid h-[21px] w-[21px] shrink-0 place-items-center rounded-full bg-mini-avatar text-[8px] font-semibold text-mini-avatar-fg">
                        {conversation.participantImageUrl ? (
                          <img
                            src={conversation.participantImageUrl}
                            alt=""
                            className="h-full w-full rounded-full object-cover"
                          />
                        ) : (
                          getInitials(conversation.participantName)
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-dark-blue">
                          {conversation.participantName || "Unknown"}
                        </p>
                        <small className="mt-0.5 block truncate text-[10px] text-muted">
                          LinkedIn{truncatePreview(conversation.lastMessageText || "") ? ` · ${truncatePreview(conversation.lastMessageText || "")}` : ""}
                        </small>
                      </div>
                      <time className="shrink-0 text-[10px] text-muted">
                        {formatRelativeTime(conversation.lastActivityAt)}
                      </time>
                    </button>
                  );
                })
              )}
              {loadingMoreConversations ? (
                <p className="px-1 py-2 text-center text-[11px] text-dark-grey">
                  Loading older chats...
                </p>
              ) : null}
              {!loadingConversations && accountMessage && accountStatus !== "not_connected" ? (
                <p className="mt-3 text-center text-xs font-medium text-dark-grey">
                  {isDisconnectedState ? "Connect your account to load inbox" : accountMessage}
                </p>
              ) : null}
              {!loadingConversations && shouldShowDisconnectedEmptyState ? (
                <div className="mt-3 flex justify-center">
                  <button
                    className="inline-flex items-center gap-2 rounded bg-bright-green px-3 py-1.5 text-xs font-semibold text-dark-black"
                    onClick={onReconnectSender}
                  >
                    <FiAtSign size={12} />
                    Reconnect account
                  </button>
                </div>
              ) : null}
            </div>
            </div>
          </aside>

          <main
            className={`relative h-full min-h-0 flex-1 flex-col overflow-hidden ${
              selectedThreadId ? "flex" : "hidden lg:flex"
            }`}
          >
            {selectedConversation ? (
              <>
                <div className="flex items-center justify-between border-b border-line bg-white px-4 py-[13px]">
                  <div className="flex min-w-0 items-center gap-2">
                    <button
                      type="button"
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[4px] border border-line bg-white text-dark-blue lg:hidden"
                      aria-label="Back to conversations"
                      onClick={() => navigate("/inbox")}
                    >
                      ←
                    </button>
                    <div className="grid h-[21px] w-[21px] shrink-0 place-items-center rounded-full bg-user-avatar text-[8px] font-semibold text-user-avatar-fg">
                      {selectedConversation.participantImageUrl ? (
                        <img
                          src={selectedConversation.participantImageUrl}
                          alt=""
                          className="h-full w-full rounded-full object-cover"
                        />
                      ) : (
                        getInitials(selectedConversation.participantName)
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-dark-blue">
                        {selectedConversation.participantName}
                      </p>
                      <small className="mt-0.5 block truncate text-[10px] text-muted">
                        {selectedConversation.campaignName || "LinkedIn"}
                      </small>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10 shrink-0 !border-line px-3.5 font-semibold text-dark-blue hover:bg-paper"
                    type="button"
                  >
                    View profile
                  </Button>
                </div>
                <div
                  ref={messagesScrollRef}
                  className="inboxScrollbar min-h-0 flex-1 overflow-y-auto bg-message-pane p-[17px]"
                >
                  {messages.length === 0 && !loadingMessages && !refreshingThread ? (
                    <p className="text-xs text-muted">No messages found for this thread.</p>
                  ) : (
                    messages.map((message) => {
                      const ownerMessage = isOwnerMessage(message);
                      return (
                        <div
                          key={message.messageUrn}
                          className={`mb-2.5 max-w-[72%] rounded-[8px] px-2.5 py-2.5 text-xs text-dark-blue ${
                            ownerMessage
                              ? "ml-auto bg-outgoing"
                              : "border border-line bg-white"
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{message.text}</p>
                          <small className="mt-1.5 block text-[9px] text-muted">
                            {formatTime(message.deliveredAt)}
                            {ownerMessage ? ` · ${senderLabel}` : " · LinkedIn"}
                          </small>
                        </div>
                      );
                    })
                  )}
                </div>
                {loadingMessages || refreshingThread ? (
                  <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/65">
                    <div className="flex flex-col items-center gap-2">
                      <img
                        src="/logo/loading.svg"
                        alt="Loading chat"
                        className="h-10 w-10 rounded-xl animate-pulse"
                      />
                      <p className="text-xs font-medium text-dark-grey">
                        {refreshingThread ? "Refreshing conversation..." : "Loading conversation..."}
                      </p>
                    </div>
                  </div>
                ) : null}
                <div className="border-t border-line bg-white px-4 py-[13px]">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="shrink-0 border-0 bg-transparent px-1 text-[20px] leading-none text-muted"
                      aria-label="Add"
                    >
                      +
                    </button>
                    <input
                      className="min-w-0 flex-1 rounded-[7px] border border-line px-2.5 py-2.5 text-sm text-dark-blue outline-none placeholder:text-muted"
                      placeholder="Write a reply…"
                      value={messageDraft}
                      onChange={(e) => setMessageDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        if ((e.nativeEvent as KeyboardEvent).isComposing) return;
                        if (e.shiftKey) return;
                        e.preventDefault();
                        if (
                          sendingMessage ||
                          !messageDraft.trim() ||
                          accountMessage.toLowerCase().includes("disconnected") ||
                          accountMessage.toLowerCase().includes("connect your account")
                        ) {
                          return;
                        }
                        void onSendMessage();
                      }}
                      disabled={
                        sendingMessage ||
                        accountMessage.toLowerCase().includes("disconnected") ||
                        accountMessage.toLowerCase().includes("connect your account")
                      }
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      className="h-10 px-3.5 font-semibold text-dark-blue"
                      onClick={onSendMessage}
                      isDisabled={
                        sendingMessage ||
                        !messageDraft.trim() ||
                        accountMessage.toLowerCase().includes("disconnected") ||
                        accountMessage.toLowerCase().includes("connect your account")
                      }
                    >
                      {sendingMessage ? "Sending..." : "Send"}
                    </Button>
                  </div>
                  {!loadingConversations && accountMessage && accountStatus !== "not_connected" ? (
                    <p className="mt-3 text-sm text-danger">{accountMessage}.</p>
                  ) : null}
                  {!loadingConversations && shouldShowDisconnectedEmptyState ? (
                    <div className="mt-3">
                      <button
                        className="inline-flex items-center gap-2 rounded bg-bright-green px-3 py-1.5 text-xs font-semibold text-dark-black"
                        onClick={onReconnectSender}
                      >
                        <FiAtSign size={12} />
                        Reconnect account
                      </button>
                    </div>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="m-auto text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-paper text-muted">
                  <FiMessageSquare size={22} />
                </div>
                <h2 className="text-2xl font-semibold text-dark-black">Start chatting</h2>
                <p className="mt-2 max-w-[420px] text-sm text-dark-grey">
                  Click on a conversation to load messages for the selected thread.
                </p>
              </div>
            )}
          </main>
          <aside className="hidden h-full min-h-0 flex-col overflow-y-auto border-l border-line p-[13px] lg:flex">
            <h2 className="text-[13px] font-semibold text-dark-blue">Prospect details</h2>
            {selectedConversation ? (
              <>
                <p className="mt-3 text-[11px] leading-[1.9] text-dark-blue">
                  <b>{selectedConversation.participantName || ""}</b>
                </p>
                <p className="text-[11px] leading-[1.9] text-dark-blue">
                  <span className="text-success">●</span> LinkedIn
                  <br />
                  <span className="text-muted">○</span> Email
                  <br />
                  <span className="text-muted">○</span> Phone
                </p>
                <hr className="my-3 border-line" />
                <small className="text-[9px] font-semibold uppercase tracking-wide text-muted">
                  Campaign
                </small>
                <p className="mt-1 text-[11px] leading-[1.9] text-dark-blue">
                  {selectedConversation.campaignName || ""}
                </p>
              </>
            ) : null}
          </aside>
        </div>
      </div>
      <ConnectLinkedinCredentialsSidebar
        isOpen={showReconnectSidebar}
        reconnectOwnerId={reconnectOwnerId}
        mode={connectSidebarMode}
        extensionId={activeExtensionId}
        onClose={() => {
          setShowReconnectSidebar(false);
          setReconnectOwnerId("");
          setConnectSidebarMode("extension");
          setActiveExtensionId("");
        }}
        onBack={() => {
          setShowReconnectSidebar(false);
          setIsExtensionDetectionLoading(false);
          setShowConnectModal(true);
        }}
        onConnected={async () => {
          setShowReconnectSidebar(false);
          setReconnectOwnerId("");
          setConnectSidebarMode("extension");
          setActiveExtensionId("");
          setAccountMessage("");
          await loadConversations({ force: true });
          await loadMessages();
        }}
      />
      <ConnectLinkedinModal
        isOpen={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        onOpenCredentialsSidebar={() => {
          setConnectSidebarMode("manual");
          setActiveExtensionId("");
          setShowReconnectSidebar(true);
          setShowConnectModal(false);
        }}
        onLoginWithExtension={async () => {
          setIsExtensionDetectionLoading(true);
          setShowConnectExtensionSidebar(true);
          const extensionInstalled = await detectExtensionInstalled();
          if (extensionInstalled) {
            setActiveExtensionId(getExtensionId());
            setConnectSidebarMode("extension");
            setShowReconnectSidebar(true);
            setShowConnectExtensionSidebar(false);
            setIsExtensionDetectionLoading(false);
            setShowConnectModal(false);
            return;
          }
          setShowConnectModal(false);
          setIsExtensionDetectionLoading(false);
        }}
      />
      <ConnectLinkedinExtensionSidebar
        isOpen={showConnectExtensionSidebar}
        onClose={() => {
          setShowConnectExtensionSidebar(false);
          setIsExtensionDetectionLoading(false);
        }}
        onBack={() => {
          setShowConnectExtensionSidebar(false);
          setIsExtensionDetectionLoading(false);
          setShowConnectModal(true);
        }}
        isLoading={isExtensionDetectionLoading}
      />
    </section>
  );
};

export default Inbox;
